import { DateTime } from 'luxon';
import { DuplicateCardsHelpers } from 'ontraccr-common';

import {
  CREATE_FORM_TEMPLATE,
  UPDATE_FORM_TEMPLATE,
  GET_FORM_TEMPLATES,
  GET_FORM_TEMPLATE_VERSION_BY_ID,
  RESTORE_FORM_TEMPLATE_VERSION,
  ARCHIVE_FORM_TEMPLATE,
  GET_FORM_TEMPLATE_DETAILS,
  DELETE_FORM_TEMPLATE,
  GET_FORM_TEMPLATE_EVENTS,
  GET_FORMS,
  GET_FORM_BY_ID,
  GET_FORM_APPROVALS,
  GET_FORM_APPROVAL_SUMMARY,
  APPROVE_FORM,
  REJECT_FORM,
  GET_ASSIGNED_FORMS,
  GET_ASSIGNED_FORM_TEMPLATES,
  SUBMIT_FORM,
  GET_FORM_STATUSES,
  UPDATE_FORM_STATUS,
  GET_FORM_TYPES,
  CREATE_CUSTOM_FORM_TABLE,
  GET_CUSTOM_FORM_TABLES,
  UPDATE_CUSTOM_FORM_TABLE,
  DELETE_CUSTOM_FORM_TABLE,
  SAVE_FORM_DRAFT,
  GET_FORM_DRAFTS,
  DELETE_FORM_DRAFT,
  MARK_INVOICE_AS_PAID,
  RESUBMIT_EMAIL,
  CANCEL_EMAIL_REMINDER,
  CANCEL_FORM,
  DELETE_FORM,
  CLEAR_SELECTED_FORM,
  COPY_FORM_TEMPLATE,
  FAVORITE_FORM_TEMPLATE,
  GET_FORM_CHILDREN,
  REASSIGN_FORM,
  REASSIGN_DRAFT,
  CREATE_CUSTOM_TYPE,
  DELETE_CUSTOM_TYPE,
  GET_EXTERNAL_FORM_TEMPLATES,
  GET_EXTERNAL_FORM_TEMPLATE_BY_ID,
  GET_USER_FORM_FILTER_VIEWS,
  CREATE_USER_FORM_FILTER_VIEW,
  UPDATE_USER_FORM_FILTER_VIEW,
  DELETE_USER_FORM_FILTER_VIEW,
  UPDATE_FORM_FILTERS,
  GET_LIGHTWEIGHT_FORMS,
  GET_CHILD_FORMS,
  GET_FORM_SNAPSHOTS,
  GET_FORM_SNAPSHOT_BY_ID,
  GET_SUB_CONTRACT_DETAILS,
  CANCEL_FORMS,
  UPDATE_FORM_READ_STATUS,
  SET_FORMS_LOADING,
  GET_ASSIGNED_TO_OTHERS_FORMS,
  HIDE_ASSIGNED_TO_OTHERS_FORM,
  SEARCH_BOARD_DUPLICATES,
} from '../../state/actionTypes';
import Analytics from '../../helpers/Analytics';
import FormsService from './forms.service';
import { counters, counterWasRecentlyCalled } from '../../helpers/counters';
import SearchService from '../../search/state/search.service';
import { convertQueryToOpenSearchQuery } from '../../search/searchHelpers';

import { reportSlowRequest } from '../../helpers/devHelpers';
import { setExternalFormPaidFlags } from '../../settings/state/settings.actions';

const {
  forms: formCounters,
} = counters;

export const createTemplate = (payload) => async (dispatch) => {
  Analytics.track('Forms/CreateTemplate');
  const { data } = await FormsService.createTemplate(payload);
  if (!data) return false;
  const {
    preview,
    formTemplate: { id, newStatuses } = {},
  } = data;
  dispatch({
    type: CREATE_FORM_TEMPLATE,
    payload: {
      formTemplate: { ...payload, id },
      preview,
      newStatuses,
    },
  });
  return true;
};

export const editTemplate = (id, payload) => async (dispatch) => {
  Analytics.track('Forms/EditTemplate');
  const { data } = await FormsService.editTemplate(id, payload);
  if (!data) return false;
  const {
    formTemplate: { newStatuses } = {},
    preview,
  } = data;
  dispatch({
    type: UPDATE_FORM_TEMPLATE,
    payload: {
      formTemplate: { id, ...payload },
      preview,
      newStatuses,
    },
  });
  return true;
};

export const getTemplates = (query) => async (dispatch) => {
  const { data } = await FormsService.getTemplates(query);
  if (!data) return false;
  dispatch({
    type: GET_FORM_TEMPLATES,
    payload: data,
  });
  return true;
};

export const archiveTemplate = (id, active = false) => async (dispatch) => {
  Analytics.track('Forms/ArchiveTemplate', { active });
  const didArchive = await FormsService.archive(id, active);
  if (!didArchive) return false;
  dispatch({
    type: ARCHIVE_FORM_TEMPLATE,
    payload: {
      id,
      active,
    },
  });
  return true;
};

export const deleteTemplate = (id) => async (dispatch) => {
  Analytics.track('FormsDeleteTemplate');
  const { data } = await FormsService.deleteTemplate(id);
  if (!data) return false;
  dispatch({
    type: DELETE_FORM_TEMPLATE,
    payload: {
      id,
    },
  });
  return true;
};

export const getTemplateDetails = (id, draftId, config = {}) => async (dispatch) => {
  Analytics.track('Forms/GetDetails');
  const { data } = await FormsService.getTemplateDetails(id, draftId);
  if (!data) return false;
  dispatch({
    type: GET_FORM_TEMPLATE_DETAILS,
    payload: {
      formTemplate: data,
      ...config,
    },
  });
  return true;
};

export const getTemplateEvents = (id) => async (dispatch) => {
  const { data } = await FormsService.getTemplateEvents(id);
  if (!data) return false;
  dispatch({
    type: GET_FORM_TEMPLATE_EVENTS,
    payload: {
      id,
      events: data || [],
    },
  });
  return true;
};

export const getTemplateVersionById = (id, versionId) => async (dispatch) => {
  const { data } = await FormsService.getTemplateVersionById(id, versionId);
  if (!data) return false;
  dispatch({
    type: GET_FORM_TEMPLATE_VERSION_BY_ID,
    payload: {
      id,
      version: data,
    },
  });
  return true;
};

export const restoreFormTemplateVersion = (id, versionId) => async (dispatch) => {
  const { data } = await FormsService.restoreFormTemplateVersion(id, versionId);
  if (!data) return false;
  dispatch({
    type: RESTORE_FORM_TEMPLATE_VERSION,
    payload: {
      id,
      versionNumber: data.versionNumber,
      snapshot: data.snapshot,
    },
  });
  return true;
};

export const getForms = (query = {}) => async (dispatch) => {
  const { merge, ...apiQuery } = query;
  dispatch({
    type: SET_FORMS_LOADING,
    payload: {
      loading: true,
    },
  });
  const startTime = Date.now();
  const { data } = await FormsService.getForms(apiQuery);
  reportSlowRequest({
    startTime,
    requestLabel: 'getForms',
    metadata: { query: apiQuery },
  });
  dispatch({
    type: SET_FORMS_LOADING,
    payload: {
      loading: false,
    },
  });
  if (!data) return false;
  dispatch({
    type: GET_FORMS,
    payload: {
      forms: data,
      merge,
    },
  });
  return true;
};

export const getLightWeightForms = (query) => async (dispatch) => {
  const { data } = await FormsService.getLightWeightForms(query);
  if (!data) return false;
  dispatch({
    type: GET_LIGHTWEIGHT_FORMS,
    payload: {
      forms: data,
      templateId: query?.templateId,
      subContractTemplateId: query?.subContractTemplateId,
    },
  });
  return true;
};

export const getFormById = (id, query) => async (dispatch) => {
  const { data } = await FormsService.getFormById(id, query);
  if (!data) return false;
  dispatch({
    type: GET_FORM_BY_ID,
    payload: {
      form: data,
    },
  });
  return data;
};

export const getFormApprovals = (params) => async (dispatch) => {
  const startTime = Date.now();
  const { data } = await FormsService.getFormApprovals(params);
  reportSlowRequest({
    startTime,
    requestLabel: 'getFormApprovals',
  });
  if (!data) return false;
  dispatch({
    type: GET_FORM_APPROVALS,
    payload: {
      approvals: data,
    },
  });
  return true;
};

export const getFormApprovalSummary = () => async (dispatch) => {
  const { data } = await FormsService.getFormApprovalSummary();
  if (!data) return false;
  dispatch({
    type: GET_FORM_APPROVAL_SUMMARY,
    payload: {
      approvals: data,
    },
  });
  return true;
};

export const approveRejectForm = (formId, payload) => async (dispatch) => {
  const isApprove = payload.approved;
  Analytics.track(`Forms/${isApprove ? 'Approve' : 'Reject'}`);
  const { data } = await FormsService.approveRejectForm(formId, payload);
  if (!data) return false;
  const { data: form } = await FormsService.getFormById(formId);
  if (!form) return false;
  dispatch({
    type: isApprove ? APPROVE_FORM : REJECT_FORM,
    payload: {
      form,
      approvalId: payload.approvalId,
    },
  });
  return true;
};

export const getAssignedForms = (userId) => async (dispatch) => {
  const { data: forms } = await FormsService.getAssigned(userId);
  if (!forms) return false;
  dispatch({
    type: GET_ASSIGNED_FORMS,
    payload: {
      forms,
    },
  });
  return true;
};

export const getAssignedFormTemplates = () => async (dispatch) => {
  if (counterWasRecentlyCalled('forms.getAssignedFormTemplates')) return true;
  formCounters.getAssignedFormTemplates = DateTime.local();
  const {
    data: formTemplates,
  } = await FormsService.getAssignedTemplates();
  if (!formTemplates) {
    formCounters.getAssignedFormTemplates = null;
    return false;
  }
  dispatch({
    type: GET_ASSIGNED_FORM_TEMPLATES,
    payload: {
      formTemplates,
    },
  });
  return true;
};

export const submitForm = (payload, isExternalForm = false, config = {}) => async (dispatch) => {
  Analytics.track('Forms/Submit');
  const { data: submittedForm } = await FormsService.submitForm(payload, isExternalForm, undefined, config);
  if (!submittedForm) return false;
  dispatch({
    type: payload.isDraft ? SAVE_FORM_DRAFT : SUBMIT_FORM,
    payload: {
      submittedForm,
      isAssignedForm: payload.id === submittedForm.id,
      data: payload,
    },
  });
  return true;
};

export const getStatuses = () => async (dispatch) => {
  const { data: statuses } = await FormsService.getFormStatuses();
  if (!statuses) return false;
  dispatch({
    type: GET_FORM_STATUSES,
    payload: {
      statuses,
    },
  });
  return true;
};

export const updateStatus = (id, newStatus) => async (dispatch) => {
  const { data } = await FormsService.updateFormStatus(id, { status: newStatus });
  if (!data) return false;
  dispatch({
    type: UPDATE_FORM_STATUS,
    payload: {
      id,
      status: newStatus,
    },
  });
  return true;
};

export const updateReadStatus = (id, isRead, locationOfForm = '', onlyInFormDrafts = false) => async (dispatch) => {
  const { data } = await FormsService.updateFormReadStatus(id, { isRead, onlyInFormDrafts });
  if (!data) return false;
  dispatch({
    type: UPDATE_FORM_READ_STATUS,
    payload: {
      id,
      isRead,
      locationOfForm,
      onlyInFormDrafts,
    },
  });
  return true;
};

export const getFormTypes = () => async (dispatch) => {
  const { data: types } = await FormsService.getFormTypes();
  if (!types) return false;
  dispatch({
    type: GET_FORM_TYPES,
    payload: {
      types,
    },
  });
  return true;
};

export const createCustomTable = (payload) => async (dispatch) => {
  Analytics.track('Forms/CreateCustomTable');
  const { data } = await FormsService.createCustomTable(payload);
  if (!data) return false;
  dispatch({
    type: CREATE_CUSTOM_FORM_TABLE,
    payload: {
      customTable: data,
    },
  });
  return true;
};

export const getCustomTables = () => async (dispatch) => {
  if (counterWasRecentlyCalled('forms.getCustomTables')) return true;
  formCounters.getCustomTables = DateTime.local();
  const { data: customTables } = await FormsService.getCustomTables();
  if (!customTables) {
    formCounters.getCustomTables = null;
    return false;
  }
  dispatch({
    type: GET_CUSTOM_FORM_TABLES,
    payload: {
      customTables,
    },
  });
  return true;
};

export const updateCustomTable = (id, payload) => async (dispatch) => {
  const { data: newData } = await FormsService.updateCustomTable(id, payload);
  if (!newData) return false;
  dispatch({
    type: UPDATE_CUSTOM_FORM_TABLE,
    payload: {
      id,
      newData,
    },
  });
  return true;
};

export const deleteCustomTable = (id) => async (dispatch) => {
  const { data } = await FormsService.deleteCustomTable(id);
  if (!data) return false;
  dispatch({
    type: DELETE_CUSTOM_FORM_TABLE,
    payload: {
      id,
    },
  });
  return true;
};

export const getDrafts = (query) => async (dispatch) => {
  const { data: drafts } = await FormsService.getDrafts(query);
  if (!drafts) return false;
  dispatch({
    type: GET_FORM_DRAFTS,
    payload: {
      drafts,
    },
  });
  return true;
};

export const deleteDraft = ({ formId, id }) => async (dispatch) => {
  const { data: drafts } = await FormsService.deleteDraft(id);
  if (!drafts) return false;
  dispatch({
    type: DELETE_FORM_DRAFT,
    payload: {
      formId, id,
    },
  });
  return true;
};

export const markInvoiceAsPaid = (id) => async (dispatch) => {
  const { data } = await FormsService.markInvoiceAsPaid(id);
  if (!data) return false;
  dispatch({
    type: MARK_INVOICE_AS_PAID,
    payload: {
      id,
    },
  });
  return true;
};

export const resubmitEmail = ({ formId, id, stepId }) => async (dispatch) => {
  const { data } = await FormsService.resubmitEmail(formId, { id, stepId });
  if (!data) return false;
  dispatch({
    type: RESUBMIT_EMAIL,
    payload: {
      steps: data,
    },
  });
  return true;
};

export const cancelEmailReminder = ({ formId, scheduleId }) => async (dispatch) => {
  const { data } = await FormsService.cancelEmailReminder(formId, scheduleId);
  if (!data) return false;
  dispatch({
    type: CANCEL_EMAIL_REMINDER,
    payload: {
      formId,
      scheduleId,
      cancelReason: 'user',
      cancelledByUserId: data.userId,
    },
  });
  return true;
};

export const cancelWorkflow = (id) => async (dispatch) => {
  const { data } = await FormsService.cancelWorkflow(id);
  if (!data) return false;
  dispatch({
    type: CANCEL_FORM,
    payload: {
      id,
    },
  });
  return true;
};

export const closeForm = (id, formValue) => async (dispatch) => {
  const { data } = await FormsService.closeForm(id);
  if (!data) return false;
  dispatch({
    type: CANCEL_FORM,
    payload: {
      id,
      isPOClose: true,
      formValue,
    },
  });
  return true;
};

export const deleteForm = (id) => async (dispatch) => {
  const { data } = await FormsService.deleteForm(id);
  if (!data) return false;
  dispatch({
    type: DELETE_FORM,
    payload: {
      formIds: data.formIds ?? [id],
    },
  });
  return true;
};
export const clearFormSelection = () => ({ type: CLEAR_SELECTED_FORM });

export const copyFormTemplate = (id) => async (dispatch) => {
  const { data } = await FormsService.copyFormTemplate(id);
  if (!data) return false;
  dispatch({
    type: COPY_FORM_TEMPLATE,
    payload: data,
  });
  return true;
};

export const favoriteFormTemplate = (id, isFavorite) => async (dispatch) => {
  Analytics.track('Forms/Favorite');
  const toggledFavorite = !isFavorite;
  const { data } = await FormsService.favoriteFormTemplate(id, { isFavorite: toggledFavorite });
  if (!data) return false;
  dispatch({
    type: FAVORITE_FORM_TEMPLATE,
    payload: {
      id,
      favorite: toggledFavorite,
    },
  });
  return true;
};

export const getSharedFormDraftChildren = (id) => async (dispatch) => {
  const { data: children } = await FormsService.getSharedFormDraftChildren(id);
  if (!children) return false;
  dispatch({
    type: GET_FORM_CHILDREN,
    payload: {
      children,
    },
  });
  return true;
};

export const markFormInitiated = (id) => async () => {
  const { data } = await FormsService.markFormInitiated(id);
  return !!data;
};

export const reassignForm = (id, payload) => async (dispatch) => {
  const { data } = await FormsService.reassignForm(id, payload);
  if (!data) return false;
  dispatch({
    type: REASSIGN_FORM,
    payload: data,
  });
  return true;
};

export const reassignDraft = (id, payload) => async (dispatch) => {
  const { data } = await FormsService.reassignDraft(id, payload);
  if (!data) return false;
  dispatch({
    type: REASSIGN_DRAFT,
    payload: data,
  });
  return true;
};

export const createCustomType = (payload) => async (dispatch) => {
  const { data } = await FormsService.createCustomType(payload);
  if (!data) return false;
  dispatch({
    type: CREATE_CUSTOM_TYPE,
    payload: {
      name: data,
    },
  });
  return true;
};

export const deleteCustomType = (id) => async (dispatch) => {
  const { data } = await FormsService.deleteCustomType(id);
  if (!data) return false;
  dispatch({
    type: DELETE_CUSTOM_TYPE,
    payload: {
      id,
    },
  });
  return true;
};

export const updateCustomType = (id, payload) => async (dispatch) => {
  const { data } = await FormsService.updateCustomType(id, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_CUSTOM_TYPE,
    payload: {
      id,
      name: payload.name,
    },
  });
  return true;
};

export const getExternalFormTemplates = () => async (dispatch) => {
  const { data } = await FormsService.getExternalFormTemplates();
  if (!data) return false;
  dispatch({
    type: GET_EXTERNAL_FORM_TEMPLATES,
    payload: {
      externalFormTemplates: data,
    },
  });
  return true;
};

export const getExternalFormTemplateById = (id) => async (dispatch) => {
  const { data } = await FormsService.getExternalFormTemplateById(id);
  if (!data) return false;
  dispatch({
    type: GET_EXTERNAL_FORM_TEMPLATE_BY_ID,
    payload: {
      formTemplate: data,
    },
  });
  if (Array.isArray(data.paidFlags)) {
    dispatch(setExternalFormPaidFlags(data.paidFlags));
  }
  return true;
};

export const importForm = (payload) => async () => {
  const { data } = await FormsService.importForm(payload);
  return !!data;
};

export const massApproveRejectForms = (payload) => async (dispatch) => {
  const { data } = await FormsService.massApproveRejectForms(payload);
  if (!data) return false;
  dispatch(getFormApprovals({ getFormData: true }));
  return true;
};

export const getUserFormFilterViews = () => async (dispatch) => {
  const { data } = await FormsService.getUserFormFilterViews();
  if (!data) return false;
  dispatch({
    type: GET_USER_FORM_FILTER_VIEWS,
    payload: {
      formFilterViews: data,
    },
  });
  return true;
};

export const createUserFormFilterView = (payload) => async (dispatch) => {
  const {
    data: {
      newFilterView,
      filterViews,
    } = {},
  } = await FormsService.createUserFormFilterView(payload);
  if (!newFilterView) return false;
  dispatch({
    type: CREATE_USER_FORM_FILTER_VIEW,
    payload: {
      formFilterViews: filterViews,
    },
  });
  return newFilterView;
};

export const updateUserFormFilterView = (filterViewId, payload) => async (dispatch) => {
  const { data } = await FormsService.updateUserFormFilterView(filterViewId, payload);
  if (!data) return false;
  dispatch({
    type: UPDATE_USER_FORM_FILTER_VIEW,
    payload: {
      formFilterViews: data,
    },
  });
  return true;
};

export const deleteUserFormFilterView = (filterViewId) => async (dispatch) => {
  const { data } = await FormsService.deleteUserFormFilterView(filterViewId);
  if (!data) return false;
  dispatch({
    type: DELETE_USER_FORM_FILTER_VIEW,
    payload: {
      formFilterViews: data,
    },
  });
  return true;
};

export const updateFormFilters = (filters) => ({
  type: UPDATE_FORM_FILTERS,
  payload: {
    formFilters: filters,
  },
});

export const getChildForms = (templateId) => async (dispatch) => {
  const { data } = await FormsService.getChildForms(templateId);
  if (!data) return false;
  dispatch({
    type: GET_CHILD_FORMS,
    payload: {
      childForms: data,
    },
  });
  return true;
};

export const getFormSnapshots = (formId) => async (dispatch) => {
  const { data } = await FormsService.getFormSnapshots(formId);
  if (!data) return false;
  dispatch({
    type: GET_FORM_SNAPSHOTS,
    payload: {
      formId,
      snapshots: data,
    },
  });
  return true;
};

export const getFormSnapshotById = (formId, snapshotId) => async (dispatch) => {
  const { data } = await FormsService.getFormSnapshotById(formId, snapshotId);
  if (!data) return false;
  dispatch({
    type: GET_FORM_SNAPSHOT_BY_ID,
    payload: { formId, snapshot: data },
  });
  return data;
};

export const getSubContractDetails = (subContractIds) => async (dispatch) => {
  const counterKey = subContractIds?.join(',');
  if (counterWasRecentlyCalled(`forms.getSubContractDetails.${counterKey}`)) return true;
  formCounters.getSubContractDetails[counterKey] = DateTime.local();
  const { data } = await FormsService.getSubContractDetails(subContractIds);
  if (!data) {
    formCounters.getSubContractDetails[counterKey] = null;
    return false;
  }
  dispatch({
    type: GET_SUB_CONTRACT_DETAILS,
    payload: {
      details: data,
    },
  });
  return true;
};

export const cancelForms = (formIds = []) => async (dispatch) => {
  const { data } = await FormsService.cancelForms({ formIds });
  if (!data) return false;
  dispatch({
    type: CANCEL_FORMS,
    payload: {
      formIds,
    },
  });
  return true;
};

export const getAssignedToOthersForms = () => async (dispatch) => {
  const { data: forms } = await FormsService.getForms({ isAssignedToOthers: true });
  if (!forms) return false;
  dispatch({
    type: GET_ASSIGNED_TO_OTHERS_FORMS,
    payload: {
      forms,
    },
  });
  return true;
};

export const hideAssignedToOthersForm = (formId) => async (dispatch) => {
  const { data } = await FormsService.hideAssignedToOthersForm(formId);
  if (!data) return false;
  dispatch({
    type: HIDE_ASSIGNED_TO_OTHERS_FORM,
    payload: {
      formId,
    },
  });
  return true;
};

export const searchBoardDuplicates = (query, showInDetection) => async (dispatch) => {
  const bufferedQuery = {
    ...query,
    formulas: DuplicateCardsHelpers.prepareDuplicateSearchFormulas(query.formulas),
  };
  const openSearchQuery = convertQueryToOpenSearchQuery(bufferedQuery, 'and');

  const { data } = await SearchService.searchBoards({
    query: openSearchQuery,
    queryId: undefined,
    cardTypeId: query.cardTypeId,
    boardIds: query.boardIds,
    checkAllBoardsWithSameCardType: query.checkAllBoardsWithSameCardType,
    includeAllData: true,
    showInDetection,
  });

  if (!data) return false;

  dispatch({
    type: SEARCH_BOARD_DUPLICATES,
    payload: {
      results: data || [],
    },
  });

  return true;
};

export const clearDuplicateBoardSearchResults = () => ({
  type: SEARCH_BOARD_DUPLICATES,
  payload: { results: [] },
});
